import { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Mail, Phone, Send, ExternalLink, ShieldCheck } from "lucide-react";

const SKIP_KEY = "sign_preview_skip_session";

/**
 * 🆕 Feb 2026 — Riepilogo pre-invio richiesta firma OTP.
 *
 * Mostra al mittente prima dell'invio effettivo:
 *   - Titolo del documento
 *   - Miniatura/link anteprima PDF
 *   - Email + cellulare destinatario
 *   - Checkbox "Non mostrarmi più l'anteprima per questa sessione"
 *
 * Uso:
 *   const { requestPreview, PreviewDialog } = useSignaturePreview();
 *   // 1) opzionalmente prima di inviare
 *   const ok = await requestPreview({ title, recipient_email, recipient_mobile, pdf_url });
 *   if (ok) { await api.post(...); }
 *   // 2) render <PreviewDialog />
 */
export function isPreviewSkipped() {
  try { return sessionStorage.getItem(SKIP_KEY) === "1"; } catch { return false; }
}
export function markPreviewSkipped() {
  try { sessionStorage.setItem(SKIP_KEY, "1"); } catch { /* noop */ }
}
export function clearPreviewSkipped() {
  try { sessionStorage.removeItem(SKIP_KEY); } catch { /* noop */ }
}

export default function SignaturePreviewDialog({
  open,
  onOpenChange,
  data,   // { title, recipient_name, recipient_email, recipient_mobile, pdf_url, doc_type_label, require_drawn_signature }
  onConfirm,
  loading = false,
}) {
  const [skipNext, setSkipNext] = useState(false);

  const handleConfirm = () => {
    if (skipNext) markPreviewSkipped();
    onConfirm?.();
  };

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) onOpenChange?.(v); }}>
      <DialogContent className="max-w-lg" data-testid="signature-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-600" />
            Conferma invio richiesta firma
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Titolo documento */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <FileText className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Documento</div>
              <div className="font-semibold text-slate-900 truncate" data-testid="preview-doc-title">{data.title || "—"}</div>
              {data.doc_type_label && (
                <div className="text-xs text-slate-500 mt-0.5">{data.doc_type_label}</div>
              )}
            </div>
          </div>

          {/* Anteprima / Link PDF */}
          {data.pdf_url ? (
            <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50/40 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
                <FileText className="h-4 w-4 text-indigo-600 shrink-0" />
                <span className="font-medium">Anteprima PDF</span>
                <span className="text-xs text-slate-500 truncate">— apri per verificare il contenuto</span>
              </div>
              <a
                href={data.pdf_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-800 hover:underline shrink-0"
                data-testid="preview-open-pdf"
              >
                Apri <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : (
            <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-500">
              Nessuna anteprima PDF disponibile per questo tipo di documento.
            </div>
          )}

          {/* Destinatario */}
          <div className="p-3 rounded-lg border border-slate-200 bg-white space-y-1.5">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Destinatario</div>
            {data.recipient_name && (
              <div className="text-sm font-medium text-slate-900" data-testid="preview-recipient-name">{data.recipient_name}</div>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="font-mono truncate" data-testid="preview-recipient-email">{data.recipient_email || "—"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="font-mono" data-testid="preview-recipient-mobile">{data.recipient_mobile || "—"}</span>
            </div>
          </div>

          {data.require_drawn_signature && (
            <div className="p-2.5 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
              ✍️ Il cliente dovrà anche disegnare la firma grafica nella pagina di firma.
            </div>
          )}

          {/* Skip preview toggle */}
          <label className="flex items-start gap-2 cursor-pointer select-none pt-1">
            <input
              type="checkbox"
              checked={skipNext}
              onChange={(e) => setSkipNext(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-indigo-600"
              data-testid="preview-skip-checkbox"
            />
            <span className="text-xs text-slate-600">
              Non mostrarmi più l&apos;anteprima per questa sessione
              <span className="block text-[10px] text-slate-400">Il controllo tornerà attivo al prossimo ricaricamento della pagina.</span>
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={loading}
            data-testid="preview-cancel"
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !data.recipient_email}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="preview-confirm"
          >
            <Send className="h-4 w-4 mr-2" />
            {loading ? "Invio…" : "Conferma e Invia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
